import { useEffect, useMemo, useRef, useState } from "react";
import { Loader, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { Spotlight, SpotlightActionData, closeSpotlight, openSpotlight } from "@mantine/spotlight";
import { observer } from "mobx-react";
import { AiOutlineSearch } from "react-icons/ai";
import { useLocation, useNavigate } from "react-router-dom";
import {
    buildHighlightChunks,
    createPodcastSearchIndex,
    FuzzyPodcastResult,
    getMatchIndices,
    normalizeSearchQuery,
    searchPodcastIndex,
} from "@/core/fuzzySearch";
import { consumeSearchOpenMethod, markSearchOpenMethod, searchAnalytics } from "@/core/searchAnalytics";
import { Urls } from "@/core/urls";
import { useStores } from "@/hooks/useStores";
import { EpisodeSearchAction, PodcastSearchAction, RecentSearchAction, SavedSearchAction } from "./SpotlightActions";

const MAX_PODCAST_RESULTS = 5;
const MAX_EPISODE_RESULTS = 5;

export const SpotlightSearch = observer(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const { categoryStore, collectionStore, languageStore, podcastStore, searchStore, toplistStore } = useStores();
    const [query, setQuery] = useState(searchStore.lastQuery);
    const [debouncedQuery] = useDebouncedValue(query, 300);
    const normalizedQuery = normalizeSearchQuery(query);
    const lastTrackedQueryRef = useRef("");
    const noResultsTrackedRef = useRef("");
    const openedAtRef = useRef<number | null>(null);
    const selectedResultRef = useRef(false);

    useEffect(() => {
        if (!podcastStore.stats) {
            podcastStore.loadStats().catch(console.error);
        }
    }, [podcastStore]);

    useEffect(() => {
        void searchStore.ensureSearchResults(debouncedQuery).catch((error) => {
            console.error("Failed to ensure search results", error);
        });
    }, [debouncedQuery, searchStore]);

    useEffect(() => {
        if (location.pathname === Urls.search()) {
            markSearchOpenMethod("route");
            openSpotlight();
        }
    }, [location.pathname]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === "k") {
                markSearchOpenMethod("keyboard");
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    const knownPodcasts = useMemo(() => {
        return [
            ...podcastStore.popularPodcasts,
            ...podcastStore.podcasts,
            ...(podcastStore.podcast ? [podcastStore.podcast] : []),
            ...languageStore.podcasts,
            ...categoryStore.podcasts,
            ...toplistStore.entries.map((entry) => entry.podcast).filter((podcast): podcast is Podcast => !!podcast),
            ...(collectionStore.collection?.podcasts ?? []),
        ];
    }, [
        categoryStore.podcasts,
        collectionStore.collection?.podcasts,
        languageStore.podcasts,
        podcastStore.podcast,
        podcastStore.podcasts,
        podcastStore.popularPodcasts,
        toplistStore.entries,
    ]);

    const podcastSearchIndex = useMemo(() => createPodcastSearchIndex(knownPodcasts), [knownPodcasts]);

    const localPodcastResults = useMemo(() => {
        return searchPodcastIndex(normalizedQuery, podcastSearchIndex);
    }, [normalizedQuery, podcastSearchIndex]);

    const sortedPodcastResults = useMemo(() => {
        return mergePodcastResults(localPodcastResults, searchStore.podcasts, normalizedQuery);
    }, [localPodcastResults, normalizedQuery, searchStore.podcasts]);

    const sortedEpisodeResults = useMemo(() => {
        return searchStore.episodes
            .filter((episode) => episode.podcast)
            .map((episode) => ({
                episode,
                score: rankEpisode(episode, normalizedQuery),
            }))
            .sort((a, b) => a.score - b.score);
    }, [normalizedQuery, searchStore.episodes]);

    const resultCount = sortedPodcastResults.length + sortedEpisodeResults.length;

    useEffect(() => {
        if (normalizedQuery.length < 2 || searchStore.loading) {
            return;
        }

        if (lastTrackedQueryRef.current !== normalizedQuery) {
            searchAnalytics.queried(normalizedQuery, resultCount);
            lastTrackedQueryRef.current = normalizedQuery;
        }

        if (resultCount === 0 && noResultsTrackedRef.current !== normalizedQuery) {
            searchAnalytics.noResults(normalizedQuery);
            noResultsTrackedRef.current = normalizedQuery;
        }
    }, [normalizedQuery, resultCount, searchStore.loading]);

    const bestMatchType = useMemo(() => {
        if (!normalizedQuery) {
            return null;
        }

        const topPodcast = sortedPodcastResults[0];
        const topEpisode = sortedEpisodeResults[0];

        if (topPodcast && (!topEpisode || topPodcast.score <= topEpisode.score)) {
            return "podcast";
        }

        if (topEpisode) {
            return "episode";
        }

        return null;
    }, [normalizedQuery, sortedEpisodeResults, sortedPodcastResults]);

    const savedSearchActions: SpotlightActionData[] = searchStore.savedSearches.slice(0, 20).map((savedSearch) => ({
        id: savedSearch.id,
        label: savedSearch.query,
        description: "Sparad sökning",
        keywords: [savedSearch.query],
        onClick: () => {
            searchAnalytics.historyUsed("saved", savedSearch.query);
            searchStore.markSavedSearchUsed(savedSearch.id);
            setQuery(savedSearch.query);
        },
        children: (
            <SavedSearchAction
                onRemove={() => searchStore.removeSavedSearch(savedSearch.id)}
                savedSearch={savedSearch}
            />
        ),
    }));

    const recentSearchActions: SpotlightActionData[] = searchStore.recentSearches.slice(0, 10).map((recentSearch) => ({
        id: recentSearch.id,
        label: recentSearch.query,
        description: `${recentSearch.resultCount} träffar`,
        keywords: [recentSearch.query],
        onClick: () => {
            searchAnalytics.historyUsed("recent", recentSearch.query);
            setQuery(recentSearch.query);
        },
        children: (
            <RecentSearchAction
                onRemove={() => searchStore.removeRecentSearch(recentSearch.id)}
                recentSearch={recentSearch}
            />
        ),
    }));

    const clearRecentSearchesAction: SpotlightActionData[] =
        normalizedQuery.length === 0 && searchStore.recentSearches.length > 0
            ? [
                  {
                      id: "recent-searches-clear",
                      label: "Rensa senaste sökningar",
                      onClick: () => {
                          searchStore.clearRecentSearches();
                      },
                  },
              ]
            : [];

    const isCurrentQuerySaved = searchStore.savedSearches.some(
        (search) => search.query.toLocaleLowerCase() === normalizedQuery.toLocaleLowerCase()
    );

    const saveSearchAction: SpotlightActionData[] =
        normalizedQuery.length >= 2 && resultCount > 0
            ? [
                  {
                      id: "save-current-search",
                      label: isCurrentQuerySaved ? "Sökningen är sparad" : `Spara "${normalizedQuery}"`,
                      disabled: isCurrentQuerySaved,
                      onClick: () => {
                          searchAnalytics.saved(normalizedQuery);
                          searchStore.saveSearch(normalizedQuery);
                      },
                  },
              ]
            : [];

    const podcastActions: SpotlightActionData[] = (
        normalizedQuery.length >= 2
            ? sortedPodcastResults.map((result) => result.podcast)
            : podcastStore.popularPodcasts
    )
        .slice(0, MAX_PODCAST_RESULTS)
        .map((podcast, index) => {
            const result = sortedPodcastResults.find((item) => item.podcast.id === podcast.id);

            return {
                id: `podcast-${podcast.id}`,
                label: podcast.name,
                description: podcast.author?.name,
                keywords: [podcast.slug, podcast.author?.name ?? ""],
                onClick: () => {
                    selectedResultRef.current = true;
                    searchAnalytics.resultClicked("podcast", index + 1, normalizedQuery);
                    navigate(Urls.podcastHome(podcast.slug));
                    closeSpotlight();
                },
                children: (
                    <PodcastSearchAction
                        authorChunks={result?.authorChunks}
                        isBestMatch={bestMatchType === "podcast" && index === 0}
                        podcast={podcast}
                        titleChunks={result?.titleChunks}
                    />
                ),
            };
        });

    const episodeActions: SpotlightActionData[] = sortedEpisodeResults
        .slice(0, MAX_EPISODE_RESULTS)
        .map(({ episode }, index) => ({
            id: `episode-${episode.id}`,
            label: episode.title,
            description: episode.podcast?.name,
            keywords: [episode.slug, episode.podcast?.name ?? ""],
            onClick: () => {
                if (!episode.podcast) {
                    return;
                }

                selectedResultRef.current = true;
                searchAnalytics.resultClicked("episode", index + 1, normalizedQuery);
                navigate(Urls.podcastEpisode(episode.podcast.slug, episode.slug));
                closeSpotlight();
            },
            children: (
                <EpisodeSearchAction
                    episode={episode}
                    isBestMatch={bestMatchType === "episode" && index === 0}
                    subtitleChunks={buildHighlightChunks(episode.podcast?.name ?? "Okänd podcast", normalizedQuery)}
                    titleChunks={buildHighlightChunks(episode.title, normalizedQuery)}
                />
            ),
        }));

    const actions = [
        { group: "Sparade sökningar", actions: savedSearchActions },
        { group: "Senaste sökningar", actions: recentSearchActions },
        { group: "Populära", actions: normalizedQuery.length === 0 ? podcastActions : [] },
        { group: "Poddar", actions: normalizedQuery.length >= 2 ? podcastActions : [] },
        { group: "Avsnitt", actions: normalizedQuery.length >= 2 ? episodeActions : [] },
        { group: "Hantera", actions: [...saveSearchAction, ...clearRecentSearchesAction] },
    ].filter((group) => group.actions.length > 0);

    return (
        <Spotlight
            actions={actions}
            nothingFound={
                searchStore.error ? (
                    <Text aria-live="polite" size="sm">
                        {searchStore.error}
                    </Text>
                ) : normalizedQuery.length > 0 ? (
                    <Text aria-live="polite" size="sm">
                        Inga resultat hittades...
                    </Text>
                ) : (
                    <Text aria-live="polite" size="sm">
                        Börja skriva eller välj en populär podcast.
                    </Text>
                )
            }
            clearQueryOnClose={false}
            closeOnActionTrigger
            highlightQuery
            maxHeight={420}
            onQueryChange={setQuery}
            onSpotlightOpen={() => {
                openedAtRef.current = Date.now();
                selectedResultRef.current = false;
                searchAnalytics.opened(consumeSearchOpenMethod());
            }}
            onSpotlightClose={() => {
                if (normalizedQuery.length >= 2) {
                    searchStore.addRecentSearch(normalizedQuery, resultCount);
                }

                searchAnalytics.closed(
                    selectedResultRef.current,
                    openedAtRef.current ? Date.now() - openedAtRef.current : 0
                );
                openedAtRef.current = null;
            }}
            query={query}
            radius="md"
            scrollable
            searchProps={{
                "aria-busy": searchStore.loading,
                leftSection: searchStore.loading ? <Loader size="xs" /> : <AiOutlineSearch size={18} />,
                placeholder: "Sök efter poddar och avsnitt...",
            }}
            shortcut={["mod + K"]}
            transitionProps={{ duration: 150, transition: "fade" }}
        />
    );
});

type RankedPodcastResult = {
    authorChunks: ReturnType<typeof buildHighlightChunks>;
    podcast: Podcast;
    score: number;
    titleChunks: ReturnType<typeof buildHighlightChunks>;
};

function mergePodcastResults(
    localResults: FuzzyPodcastResult[],
    serverResults: Podcast[],
    query: string
): RankedPodcastResult[] {
    const merged = new Map<number, RankedPodcastResult>();

    localResults.forEach((result) => {
        merged.set(result.item.id, {
            authorChunks: buildHighlightChunks(
                result.item.author?.name ?? "Okänd upphovsman",
                query,
                getMatchIndices(result.matches, "author.name")
            ),
            podcast: result.item,
            score: rankPodcast(result.item, query, result.score),
            titleChunks: buildHighlightChunks(result.item.name, query, getMatchIndices(result.matches, "name")),
        });
    });

    serverResults.forEach((podcast) => {
        const ranked: RankedPodcastResult = {
            authorChunks: buildHighlightChunks(podcast.author?.name ?? "Okänd upphovsman", query),
            podcast,
            score: rankPodcast(podcast, query),
            titleChunks: buildHighlightChunks(podcast.name, query),
        };

        const existing = merged.get(podcast.id);
        if (!existing || ranked.score < existing.score) {
            merged.set(podcast.id, ranked);
        }
    });

    return Array.from(merged.values()).sort((a, b) => a.score - b.score);
}

function rankPodcast(podcast: Podcast, query: string, fuzzyScore?: number) {
    const normalizedQuery = normalizeSearchQuery(query).toLocaleLowerCase();
    if (!normalizedQuery) {
        return Number.POSITIVE_INFINITY;
    }

    const name = podcast.name.toLocaleLowerCase();
    const author = (podcast.author?.name ?? "").toLocaleLowerCase();
    const popularityBonus = podcast.count ? Math.min(podcast.count / 100000, 0.05) : 0;

    if (name === normalizedQuery) {
        return 0 - popularityBonus;
    }

    if (name.startsWith(normalizedQuery)) {
        return 0.08 - popularityBonus;
    }

    if (name.includes(normalizedQuery)) {
        return 0.16 - popularityBonus;
    }

    if (author.includes(normalizedQuery)) {
        return 0.26 - popularityBonus;
    }

    return (fuzzyScore ?? 0.6) + 0.2 - popularityBonus;
}

function rankEpisode(episode: Episode, query: string) {
    const normalizedQuery = normalizeSearchQuery(query).toLocaleLowerCase();
    const title = episode.title.toLocaleLowerCase();
    const podcastName = (episode.podcast?.name ?? "").toLocaleLowerCase();

    if (title === normalizedQuery) {
        return 0.04;
    }

    if (title.startsWith(normalizedQuery)) {
        return 0.14;
    }

    if (title.includes(normalizedQuery)) {
        return 0.22;
    }

    if (podcastName.includes(normalizedQuery)) {
        return 0.34;
    }

    return 0.6;
}
